import React from "react";
// The same pair the table rows wear — see `ClientAppointmentsTab` for why a
// control named for payments is the right one here.
import PaymentAnswer from "../../payments/components/PaymentAnswer";
import {
  appointmentTimeLoud,
  durationLabel,
  isAwaitingAnswer,
} from "../appointments";
import type { GateProps } from "../../billing/hooks/useSubscriptionGate";
import type {
  AppointmentAnswer,
  ClientAppointment,
} from "../types/appointments.types";

export interface NextAppointmentBannerProps {
  /**
   * Today's appointment that still wants something — see `nextToday`, which is
   * what decides that. The screen draws nothing at all where there is none, so
   * this is never absent.
   */
  appointment: ClientAppointment;
  /** Off, with its reason, while the workspace has no plan behind it. */
  gate: GateProps;
  /**
   * Opens the dialog that says what became of the appointment. Only ever
   * called once its time has passed.
   */
  onAnswer: (answer: AppointmentAnswer) => void;
}

/**
 * The strip across the top of a client's screen: you are seeing this person
 * today, at this time, and here is the way in.
 *
 * Above the tabs rather than inside the Appointment one, because it is not
 * about the tab. Somebody who opened this client to look at their plan still
 * needs to know there is a call with them in two hours, and a strip that only
 * appeared once they had already gone looking for it would be telling them
 * something they had just found out.
 *
 * Today only. A banner pinned to the top of the screen for something a
 * fortnight out is a banner that is always there, which is a banner nobody
 * reads — the week ahead is the carousel's job, and the whole history is the
 * table's.
 */
const NextAppointmentBanner: React.FC<NextAppointmentBannerProps> = ({
  appointment,
  gate,
  onAnswer,
}) => {
  // Its time has come and gone with nobody saying what happened. The strip
  // stops being a way in and becomes the question — which is the whole reason
  // it stays up rather than clearing itself the moment the call begins: an
  // appointment nobody marked is the easiest thing in a day to forget, and
  // this is where it will be noticed.
  const awaiting = isAwaitingAnswer(appointment);
  const joinable =
    !awaiting && appointment.type === "Online" && !!appointment.meetingUrl;

  return (
    <section
      aria-label="Next appointment today"
      className="flex-shrink-0 flex flex-wrap items-center justify-between gap-x-6 gap-y-2 rounded-md border border-[#BFEFF3] bg-[#F2FCFD] px-5 sm:px-6 py-3.5"
    >
      <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[14px] 3xl:text-[15px]">
        <span className="font-medium text-ink">Next appointment</span>
        {/* "Today" rather than the date: the strip only ever draws one that is,
            and a reader who has to work out that 26 Sep is today has been made
            to do the strip's job. */}
        <span className="font-medium text-[#00A5B4]">
          Today, {appointmentTimeLoud(appointment.startsAt)}
        </span>
        <span aria-hidden="true" className="text-ink-subtle">
          •
        </span>
        <span className="text-ink-muted">
          {durationLabel(appointment.durationMinutes)}
        </span>
        {/* An offline appointment has nothing to press, so the strip says what
            kind it is where the Join would have been — otherwise the only
            difference between a call and a sitting is a missing button, which
            reads as something failing to load. */}
        {!joinable && !awaiting && (
          <>
            <span aria-hidden="true" className="text-ink-subtle">
              •
            </span>
            <span className="text-ink-muted">{appointment.type}</span>
          </>
        )}
      </p>

      {/* The same two answers the row in the table below wears, in the place
          the Join held — so wherever the practitioner notices the appointment
          first, the act is the same one. */}
      {awaiting && (
        <div className="flex items-center gap-2">
          <span className="sr-only">Waiting to be marked</span>
          <PaymentAnswer
            tone="confirm"
            label="Mark today’s appointment as completed"
            gate={gate}
            onClick={() => onAnswer("completed")}
          />
          {/* The same act as calling one off before it happens — see the
              table's own pair in `ClientAppointmentsTab`. */}
          <PaymentAnswer
            tone="cancel"
            label="Cancel today’s appointment — the session did not take place"
            gate={gate}
            onClick={() => onAnswer("cancelled")}
          />
        </div>
      )}

      {/* A link and not a button: this goes somewhere, so it should open in a
          new tab on a middle click and be announced as the way out that it is.
          `noopener` because the meeting URL is whatever was typed into the
          dialog. */}
      {joinable && (
        <a
          href={appointment.meetingUrl!}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[14px] 3xl:text-[15px] font-medium text-[#00A5B4] hover:text-[#007E8C] hover:underline transition-colors"
        >
          Join
          <span className="sr-only">
            {" "}
            today’s appointment at {appointmentTimeLoud(appointment.startsAt)}
          </span>
        </a>
      )}
    </section>
  );
};

export default NextAppointmentBanner;
